/**
 * useProfile — drives the self-service Profile overlay.
 *
 * Loads GET /account/profile on mount and exposes the three mutations the
 * overlay needs: save the profile (name + mobile + address), change the
 * password, and apply a verified email change. The email-VERIFICATION
 * step itself (request code / confirm code) is reused from AuthContext
 * (requestEmailVerification / confirmEmailVerification) — this viewmodel
 * only performs the final apply once the new address is verified.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { APIROUTES } from '../../constants/routes.constants'
import { parseApiError } from '../../utils/api/parseApiError'
import type { ProfileAddress, UserProfile } from '../../models/profile.model'

export interface SaveProfileInput {
  readonly firstName: string
  readonly lastName: string
  readonly phone: string
  readonly address: {
    readonly line1: string
    readonly line2: string
    readonly city: string
    readonly state: string
    readonly pin: string
    readonly country: string
  }
}

interface ProfileBody {
  readonly success: true
  readonly data: UserProfile
}

export interface UseProfileReturn {
  readonly profile: UserProfile | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly saveProfile: (input: SaveProfileInput) => Promise<UserProfile>
  readonly changePassword: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ) => Promise<void>
  readonly applyEmailChange: (newEmail: string) => Promise<string>
}

export function useProfile(): UseProfileReturn {
  const { authedFetch } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await authedFetch(APIROUTES.ACCOUNTPROFILE)
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ProfileBody
        if (!cancelled) setProfile(body.data)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load profile')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authedFetch, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  const saveProfile = useCallback(
    async (input: SaveProfileInput): Promise<UserProfile> => {
      const res = await authedFetch(APIROUTES.ACCOUNTPROFILE, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      })
      if (!res.ok) {
        const { message } = await parseApiError(res)
        throw new Error(message)
      }
      const body = (await res.json()) as ProfileBody
      setProfile(body.data)
      return body.data
    },
    [authedFetch]
  )

  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
      confirmPassword: string
    ): Promise<void> => {
      const res = await authedFetch(APIROUTES.ACCOUNTPASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword })
      })
      if (!res.ok) {
        const { message } = await parseApiError(res)
        throw new Error(message)
      }
    },
    [authedFetch]
  )

  const applyEmailChange = useCallback(
    async (newEmail: string): Promise<string> => {
      const res = await authedFetch(APIROUTES.ACCOUNTEMAIL, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail })
      })
      if (!res.ok) {
        const { message } = await parseApiError(res)
        throw new Error(message)
      }
      const body = (await res.json()) as { success: true; data: { email: string } }
      setProfile((p) => (p === null ? p : { ...p, email: body.data.email }))
      return body.data.email
    },
    [authedFetch]
  )

  return { profile, isLoading, error, reload, saveProfile, changePassword, applyEmailChange }
}

export type { UserProfile, ProfileAddress }
